import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast, ToastContainer } from "react-toastify";
import {
  getPaymentsDashboard,
  exportPaymentsCsv,
  listCategories,
  apiErrorMessage,
} from "../../api/assessmentPayments";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import useColumnVisibility from "./useColumnVisibility";
import ColumnsMenuButton from "./ColumnsMenuButton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import AssessmentNotificationIssues from "./AssessmentNotificationIssues";
const ACCENT = "#17479e";

// Shared card shadow so the transactions table reads as part of the same set
// as the stat cards above (same recipe used in AssessmentReports.jsx).
const CARD_SHADOW = "0 6px 20px rgba(23,71,158,0.08), 0 1px 3px rgba(0,0,0,0.04)";

// Matches the "glass card" look used in AssessmentDashboard.jsx / AssessmentReports.jsx.
const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

const formatRupees = (value) =>
  `\u20B9${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDateTime = (value) => {
  if (!value) return "\u2014";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Stat card defs - same recipe as AssessmentDashboard.jsx / AssessmentReports.jsx
// (icon, iconBg gradient, borderColor + glowColor for the card shadow/top-border).
// The payments endpoint returns plain totals with no period-over-period
// figures, so the cards carry no trend badge.
const statCards = (d) => [
  {
    key: "totalRevenue",
    label: "Total Revenue",
    value: formatRupees(d?.totalRevenue),
    icon: <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#7c4dff" }} />,
    iconBg: "linear-gradient(135deg, #f3f0ff, #d1c4e9)",
    borderColor: "#9575cd",
    glowColor: "rgba(149,117,205,0.2)",
  },
  {
    key: "thisMonth",
    label: "This Month",
    value: formatRupees(d?.thisMonthRevenue),
    icon: <CalendarMonthIcon sx={{ fontSize: 18, color: "#66bb6a" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    glowColor: "rgba(129,199,132,0.2)",
  },
  {
    key: "activeSubs",
    label: "Active Subs",
    value: Number(d?.activeSubscriptions ?? 0).toLocaleString("en-IN"),
    icon: <SubscriptionsIcon sx={{ fontSize: 18, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    glowColor: "rgba(100,181,246,0.2)",
  },
  {
    key: "failedTxns",
    label: "Failed Txns (This Month)",
    value: Number(d?.failedTransactionsThisMonth ?? 0).toLocaleString("en-IN"),
    icon: <ErrorOutlineIcon sx={{ fontSize: 18, color: "#ef5350" }} />,
    iconBg: "linear-gradient(135deg, #fff2f2, #ffcdd2)",
    borderColor: "#e57373",
    glowColor: "rgba(229,115,115,0.2)",
  },
];

function StatCard({ icon, iconBg, borderColor, glowColor, value, label }) {
  return (
    <Card
      sx={{
        ...cardStyle,
        boxShadow: `0 2px 14px ${glowColor}, 0 1px 3px rgba(0,0,0,0.05)`,
        borderTop: `3px solid ${borderColor}`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)" },
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box
            sx={{
              background: iconBg,
              borderRadius: 1.5,
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.72rem" }}>
            {label}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#1a1a2e" }}>{value}</Typography>

      </CardContent>
    </Card>
  );
}

const allColumns = [
  { key: "student", label: "Student", getValue: (r) => r.studentName },
  { key: "category", label: "Course", getValue: (r) => r.categoryName },
  { key: "amount", label: "Amount", getValue: (r) => r.amount },
  { key: "date", label: "Date", getValue: (r) => r.date },
  { key: "method", label: "Method", getValue: (r) => r.method },
  { key: "status", label: "Status", cellCss: { align: "center" }, getValue: (r) => r.status },
];

// The backend labels a transaction "Success" (entitlement ACTIVE) or "Failed".
const statusCfg = {
  SUCCESS: { label: "Success", color: "#2e7d32", bg: "#e8f5e9" },
  FAILED: { label: "Failed", color: "#c62828", bg: "#ffebee" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[String(value ?? "").toUpperCase()] ?? {
    label: value ?? "\u2014",
    color: "#555",
    bg: "#f5f5f5",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: "0.68rem", color: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function AssessmentPayments() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { visibleColumns, toggleColumn, visibleColumnDefs } = useColumnVisibility(
    "assessmentPaymentsHiddenColumns",
    allColumns,
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categories, setCategories] = useState([]);
  // Kept across loads so the Method dropdown does not empty out when a filter
  // happens to return zero rows (the backend lists methods present in the data).
  const [methods, setMethods] = useState([]);

  const filters = { categoryId: categoryFilter, method: methodFilter, status: statusFilter };
  const hasActiveFilters = Boolean(categoryFilter || methodFilter || statusFilter);

  useEffect(() => {
    listCategories({ size: 100 })
      .then((data) => setCategories(data.content || []))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load courses")));
  }, []);

  useEffect(() => {
    setLoading(true);
    getPaymentsDashboard({ page, size: rowsPerPage, ...filters })
      .then((data) => {
        setSummary(data);
        if (data?.availableMethods?.length) setMethods(data.availableMethods);
        setTransactions(data?.transactions?.content || []);
        setTotalElements(data?.transactions?.totalElements ?? 0);
      })
      .catch((err) => {
        setTransactions([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load payments"));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, categoryFilter, methodFilter, statusFilter]);

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const clearFilters = () => {
    setCategoryFilter("");
    setMethodFilter("");
    setStatusFilter("");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    setExporting(true);
    exportPaymentsCsv(filters)
      .then(() => toast.success("Transactions exported."))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to export transactions")))
      .finally(() => setExporting(false));
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.75,
          mb: 2.5,
        }}
      >
        {statCards(summary).map((card) => (
          <StatCard key={card.key} {...card} />
        ))}
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: CARD_SHADOW, border: "1px solid #f0f1f6", mb: 2.5 }}>
        <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#1a1a2e" }}>
            Recent Transactions
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ColumnsMenuButton allColumns={allColumns} visibleColumns={visibleColumns} onToggle={toggleColumn} />
            <LoadingButton
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              loading={exporting}
              disableElevation
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                backgroundColor: "#eef0f5",
                color: "#444",
                "&:hover": { backgroundColor: "#e2e5ee" },
              }}
            >
              Export CSV
            </LoadingButton>
          </Box>
        </CardContent>
        <CardContent sx={{ pt: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Course</InputLabel>
            <Select label="Course" value={categoryFilter} onChange={changeFilter(setCategoryFilter)}>
              <MenuItem value="">All Courses</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Method</InputLabel>
            <Select label="Method" value={methodFilter} onChange={changeFilter(setMethodFilter)}>
              <MenuItem value="">All Methods</MenuItem>
              {methods.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={changeFilter(setStatusFilter)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Success">Success</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button onClick={clearFilters} sx={{ textTransform: "none", fontWeight: 600 }}>
              Clear filters
            </Button>
          )}
          <Tooltip
            title="The summary cards above always show totals across all payments. Filters apply to the transactions table and the CSV export."
            arrow
          >
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", cursor: "help", ml: "auto" }}>
              Summary cards are not filtered
            </Typography>
          </Tooltip>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: CARD_SHADOW, borderRadius: 3, border: "1px solid #f0f1f6" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length} />
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No transactions found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((row, index) => (
                  <TableRow
                    key={`${page}-${index}`}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          const value = c.getValue(row);
                          if (c.key === "student") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{value ?? "\u2014"}</Typography>;
                          }
                          if (c.key === "amount") return value == null ? "\u2014" : formatRupees(value);
                          if (c.key === "date") return formatDateTime(value);
                          if (c.key === "status") return <StatusChip value={value} />;
                          return value ?? "\u2014";
                        })()}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <AssessmentNotificationIssues />
    </Box>
  );
}

export default AssessmentPayments;
